import services from '@/services/index.ts';
import { useAuth } from '@clerk/clerk-react';
import axios from 'axios';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { toast } from "sonner";
import { Organization } from "@/types/org.js";
import backendApis from "@/services/backendApis.js";
import { getLocalStorage, setLocalStorage } from "@/helpers/helperFunctions.js";
import constants from "@/constants/constants.js";
import { Role, UserProfile } from "@/types/main.js";

interface AuthContextType {
  isAdmin: boolean;
  logout: () => void;
  organization: Organization | null;
  ownUserId: string | null;
  setUser: React.Dispatch<React.SetStateAction<any | null>>;
  setWorkflowId: React.Dispatch<React.SetStateAction<string>>;
  user: UserProfile | null;
  users: UserProfile[];
  validateAuth: () => Promise<void>;
  workflowId: string;
  workflows: any[];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = (
  {
    children,
  }) => {
  const [org, setOrg] = useState<Organization | null>(null)
  const [user, setUser] = useState<UserProfile | null>(null);
  const { getToken } = useAuth();
  const [workflowId, setWorkflowId] = useState<string | null>(null);
  const [workflows, setWorkflows] = useState<any[]>([]);
  const [ownUserId, setOwnUserId] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [users, setUsers] = useState<UserProfile[]>([]);
  const validatingRef = useRef(false);

  const listWorkflows = async () => {
    try {
      const res = await backendApis.listWorkflows();
      const items = res.data.workflows;
      const filterMainWorkflows = items.filter(a => a.version_tag === 'main')
      setWorkflows(filterMainWorkflows);
      if (!workflowId) {
        const preferredWId = getLocalStorage(constants.workflowIdPrefKey);
        if (preferredWId && filterMainWorkflows.some(a => a.workflow_id === preferredWId)) {
          updateWorkflowId(preferredWId);
        } else {
          updateWorkflowId(filterMainWorkflows[0]?.workflow_id);
        }
      }
    } catch (e) {
      console.error(e);
    }
  }

  const updateWorkflowId = (id: string) => {
    setWorkflowId(id);
    setLocalStorage(constants.workflowIdPrefKey, id);
  }

  const setAuthToken = (token: string) => {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  };

  const getUserProfile = async (): Promise<UserProfile> => {
    try {
      const token = await getToken();
      const response = await backendApis.userProfile({}, {
        Authorization: `Bearer ${token}`
      });
      if (response.err) {
        throw new Error(response.err);
      }
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  const fetchOrg = async (orgId: string) => {
    try {
      const res = await services.backendApis.getOrganization({ organization_id: orgId });
      if (res.err) {
        throw new Error(res.err);
      }
      if (res.data.statusCode === 200) {
        setOrg(res.data.organization);
      } else {
        toast.error('Fetch org failed');
      }
    } catch (error) {
      console.error('Fetch org failed:', error);
    }
  }

  const logout = () => {
    setUser(null);
  };

  const validateAuth = async () => {
    if (validatingRef.current) return;
    validatingRef.current = true;
    try {
      const token = await getToken();
      if (!token) {
        throw new Error('Token not found');
      }
      const res = await services.authApis.authValidate({
        token,
      });
      if (res.err) {
        throw new Error(res.err);
      }
      const responseData = res.data;
      const userId = responseData.user_id;
      const isAdmin = responseData.is_admin;
      setOwnUserId(userId);
      setAuthToken(token);
      if (isAdmin) {
        setIsAdmin(true);
      }
      const profile = await getUserProfile();
      if (profile) {
        setUser(profile);
      }
      initiate();
    } catch (error) {
      console.error('Auth check failed:', error);
      logout();
    } finally {
      validatingRef.current = false;
    }
  };

  const updateToken = async () => {
    try {
      const token = await getToken();
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getUsersList = async () => {
    try {
      const token = await getToken();
      if (!token) {
        setTimeout(() => {
          getUsersList();
        }, 1000);
      }
      const params = {}
      const response = await backendApis.usersList(params, {
        Authorization: `Bearer ${token}`,
        'x-on-behalf-of': ''
      })
      const items: UserProfile[] = response.data
      if (response.err || !items) {
        throw new Error(response.err)
      }
      setUsers(items)
    } catch (error) {
      console.error('Error fetching users:', error)
    }
  }

  const initiate = async () => {
    setInterval(() => {
      updateToken();
    }, 27_500);
  }

  useEffect(() => {
    if (user) {
      listWorkflows();
      fetchOrg(user.organization_id);
    }
  }, [user]);

  useEffect(() => {
    if (ownUserId && isAdmin) {
      getUsersList();
    }
  }, [ownUserId, isAdmin]);

  return (
    <AuthContext.Provider
      value={{
        isAdmin,
        logout,
        organization: org,
        ownUserId,
        setUser,
        setWorkflowId: updateWorkflowId,
        user,
        users,
        validateAuth,
        workflowId,
        workflows
      } as AuthContextType}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useKayAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
